import { useNavigate } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import {
  useGetInvestorPitchDeckDownloadMutation,
  useGetInvestorPitchDocumentDownloadMutation,
  useGetInvestorPitchVideoUrlMutation,
  useInvestorPitchDetailQuery,
  useInvestorQuery,
  useRequestInvestorEvaluationMutation,
  useStartupConfigQuery,
} from '@/hooks/api'
import { DataRoomContent } from './data-room-content'

interface InvestorPitchDetailProps {
  pitchId: string
}

export function InvestorPitchDetail({ pitchId }: InvestorPitchDetailProps) {
  const navigate = useNavigate()

  const { data: investorResponse } = useInvestorQuery()
  const investorId = investorResponse?.data?.id

  const {
    data: pitchDetailResponse,
    isLoading,
    error,
  } = useInvestorPitchDetailQuery(investorId, pitchId)

  const { data: configResponse } = useStartupConfigQuery()
  const productStages = configResponse?.data?.productStages ?? []

  const { mutateAsync: getVideoUrl } = useGetInvestorPitchVideoUrlMutation()
  const { mutateAsync: fetchInvestorPitchDocumentUrl } =
    useGetInvestorPitchDocumentDownloadMutation()
  const { mutateAsync: fetchPitchDeckUrl } = useGetInvestorPitchDeckDownloadMutation()
  const requestEvalMutation = useRequestInvestorEvaluationMutation({
    investorId,
    pitchId,
  })

  const handleBack = () => {
    navigate({ to: '/pitches' })
  }

  if (isLoading || !investorId) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !pitchDetailResponse?.data) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <ErrorBanner message={error?.message || 'Failed to load pitch'} />
      </div>
    )
  }

  return (
    <DataRoomContent
      data={pitchDetailResponse.data}
      productStages={productStages}
      onRequestInvestorEvaluation={() => {
        if (investorId) {
          requestEvalMutation.mutate({
            path: { id: investorId, pitchId },
          })
        }
      }}
      isRequestingEvaluation={requestEvalMutation.isPending || requestEvalMutation.isSuccess}
      fetchVideoUrl={
        pitchDetailResponse.data.pitchSession?.videoKey && investorId
          ? async () => {
              const response = await getVideoUrl({ path: { id: investorId, pitchId } })
              if (response.data?.url) return response.data.url
              throw new Error('Failed to get video URL')
            }
          : undefined
      }
      fetchPitchDeckUrl={
        pitchDetailResponse.data.pitchSession?.pitchDeck && investorId
          ? async () => {
              const response = await fetchPitchDeckUrl({ path: { id: investorId, pitchId } })
              if (response.data?.downloadUrl) return response.data.downloadUrl
              throw new Error('Failed to get download URL')
            }
          : undefined
      }
      fetchDocumentUrl={async (documentId) => {
        const response = await fetchInvestorPitchDocumentUrl({
          path: { id: investorId, pitchId, documentId },
        })
        if (response.data?.downloadUrl) {
          return response.data.downloadUrl
        }
        throw new Error('Failed to get download URL')
      }}
      footer={
        <div className="mt-6 flex justify-end">
          <Button onClick={handleBack}>Go Back</Button>
        </div>
      }
    />
  )
}
